import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, X, Mail, Pencil } from "lucide-react";
import { useState } from "react";
import { useClaimAssignees } from "@/hooks/use-claim";
import type {
    ClaimDetailed,
    ClaimAssignee,
    ClaimPendingInvite,
    UpdateClaimAccessRequestBlockedModulesEnum,
} from "@/api/generated/client/models";
import { AssigneeEditDialog } from "@/components/claims/assignees/AssigneeEditDialog";
import { InviteUsersDialog } from "@/components/claims/assignees/InviteUsersDialog";
import {
    getBlockedModules,
    getNextBlockedModules,
} from "@/lib/claim-assignee-module-utils";
import { useClaimAssigneeInviteState } from "@/hooks/use-claim-assignee-invite-state";

interface SidebarAssigneeSectionProps {
    claim: ClaimDetailed;
}

export function SidebarAssigneeSection({ claim }: SidebarAssigneeSectionProps) {
    const {
        selectedAssignableUserId,
        setSelectedAssignableUserId,
        inviteEmail,
        setInviteEmail,
        shareLevel,
        setShareLevel,
        hiddenModules,
        toggleHiddenModule,
        submitInvite,
    } = useClaimAssigneeInviteState();
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [editingShareId, setEditingShareId] = useState<number | null>(null);

    const {
        assignableUsers,
        isLoadingAssignable,
        isAssigning,
        isRevoking,
        isInvitingByEmail,
        assignUser,
        revokeUser,
        inviteByEmail,
        updateUserAccess,
        revokeInvite,
        isUpdatingAccess,
        isRevokingInvite,
    } = useClaimAssignees(claim.id, { assignableEnabled: isInviteModalOpen });

    const canWrite = claim.canWrite ?? false;

    const handleAccessLevelChange = async (assignee: ClaimAssignee, nextLevel: "ro" | "rw") => {
        await updateUserAccess({
            shareId: assignee.shareId,
            accessLevel: nextLevel,
            blockedModules: getBlockedModules(assignee),
        });
    };

    const handleModuleToggle = async (
        assignee: ClaimAssignee,
        module: UpdateClaimAccessRequestBlockedModulesEnum,
        checked: boolean,
    ) => {
        await updateUserAccess({
            shareId: assignee.shareId,
            blockedModules: getNextBlockedModules(getBlockedModules(assignee), module, checked),
        });
    };

    const closeInviteModal = () => {
        if (isInvitingByEmail || isAssigning) return;
        setIsInviteModalOpen(false);
    };

    const assignees: ClaimAssignee[] = claim.assignees ?? [];
    const pendingInvites: ClaimPendingInvite[] = claim.pendingInvites ?? [];
    const editingAssignee = assignees.find((assignee) => assignee.shareId === editingShareId) ?? null;

    return (
        <Card className="shadow-xs bg-card">
            <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    <h2 className="text-sm font-semibold">Assigned Users</h2>
                    {assignees.length > 0 && (
                        <Badge variant="secondary" className="ml-auto text-xs">
                            {assignees.length}
                        </Badge>
                    )}
                </div>
            </CardHeader>

            <CardContent className="space-y-3">
                {assignees.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No users assigned to this claim.</p>
                ) : (
                    <div className="space-y-2">
                        {assignees.map((assignee) => (
                            <div key={assignee.userId} className="rounded-md border bg-muted/20 p-2">
                                <div className="flex items-center gap-1.5">
                                    <Badge variant="secondary" className="flex items-center gap-1 text-xs py-1 px-2">
                                        <span className="truncate max-w-[120px]" title={assignee.email}>
                                            {assignee.name || assignee.email}
                                        </span>
                                        {assignee.accessLevel === "ro" && (
                                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Read only</span>
                                        )}
                                    </Badge>

                                    <div className="ml-auto flex items-center gap-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6"
                                            onClick={() =>
                                                setEditingShareId((current) =>
                                                    current === assignee.shareId ? null : assignee.shareId,
                                                )
                                            }
                                            disabled={!canWrite}
                                            aria-label={`Edit ${assignee.name || assignee.email}`}
                                            title="Edit"
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 hover:bg-destructive/20 rounded-full"
                                            onClick={() => revokeUser(assignee.shareId)}
                                            disabled={isRevoking || !canWrite}
                                            aria-label={`Remove ${assignee.name || assignee.email} from claim`}
                                            title="Remove user"
                                        >
                                            <X className="h-3 w-3" />
                                        </Button>
                                    </div>
                                </div>

                            </div>
                        ))}
                    </div>
                )}

                <AssigneeEditDialog
                    assignee={editingAssignee}
                    canWrite={canWrite}
                    isUpdatingAccess={isUpdatingAccess}
                    onClose={() => setEditingShareId(null)}
                    onAccessLevelChange={handleAccessLevelChange}
                    onModuleToggle={handleModuleToggle}
                />

                {pendingInvites.length > 0 && (
                    <div className="space-y-2 rounded-md border bg-muted/10 p-2">
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Pending invites</p>
                        <div className="space-y-2">
                            {pendingInvites.map((invite) => (
                                <div key={invite.inviteId} className="flex items-center gap-2 rounded-md border bg-background p-2">
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-xs font-medium" title={invite.email}>{invite.email}</p>
                                        <p className="text-[11px] text-muted-foreground">
                                            {invite.accessLevel === "ro" ? "Read only" : "Can edit"}
                                        </p>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 hover:bg-destructive/20 rounded-full"
                                        onClick={() => revokeInvite(invite.inviteId)}
                                        disabled={isRevokingInvite || !canWrite}
                                        aria-label={`Revoke invite for ${invite.email}`}
                                        title="Revoke invite"
                                    >
                                        <X className="h-3 w-3" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="pt-2 border-t">
                    <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-full"
                        onClick={() => setIsInviteModalOpen(true)}
                        disabled={!canWrite}
                    >
                        <Mail className="h-3.5 w-3.5 mr-1.5" />
                        Invite Users
                    </Button>
                </div>

                <InviteUsersDialog
                    open={isInviteModalOpen}
                    canWrite={canWrite}
                    isAssigning={isAssigning}
                    isInvitingByEmail={isInvitingByEmail}
                    isLoadingAssignable={isLoadingAssignable}
                    assignableUsers={assignableUsers}
                    selectedAssignableUserId={selectedAssignableUserId}
                    inviteEmail={inviteEmail}
                    shareLevel={shareLevel}
                    hiddenModules={hiddenModules}
                    onSetSelectedAssignableUserId={setSelectedAssignableUserId}
                    onSetInviteEmail={setInviteEmail}
                    onSetShareLevel={setShareLevel}
                    onToggleHiddenModule={toggleHiddenModule}
                    onInvite={async () => {
                        await submitInvite({
                            assignUser,
                            inviteByEmail,
                            onSuccess: () => setIsInviteModalOpen(false),
                        });
                    }}
                    onRequestClose={closeInviteModal}
                    onOpen={() => setIsInviteModalOpen(true)}
                />
            </CardContent>
        </Card>
    );
}
